// The master refresh token belongs to the native shell. All staff API clients
// share one short-lived access-token request, including after a late bootstrap.
const RESULT_EVENT = 'cc:native-refresh-result';
const BOOTSTRAP_EVENT = 'cc_native_session_bootstrap';
let refreshPromise: Promise<{data: {access_token: string}}> | null = null;
let probePromise: Promise<boolean> | null = null;

function bridge(): any { return typeof window === 'undefined' ? undefined : (window as any).ReactNativeWebView; }
function capabilities(): any { return typeof window === 'undefined' ? {} : (window as any).__CC_NATIVE_CAPABILITIES__ || {}; }
function epoch(): number | undefined {
    const value = typeof window === 'undefined' ? undefined : (window as any).__CC_NATIVE_SESSION_CONTEXT__?.sessionEpoch;
    return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}
export function usesNativeRefreshBroker(): boolean {
    const cap = capabilities();
    return !!bridge() && cap.protocolVersion === 2 && cap.supportsNativeRefreshBroker === true && cap.nativeSessionMode === 'host-only-v1' && epoch() !== undefined;
}
function legacy(): boolean {
    const cap = capabilities();
    return cap.protocolVersion === 1 && cap.supportsSessionBundle === true && cap.supportsNativeRefreshBroker !== true && epoch() !== undefined;
}
function failure(code: string, status = 503): any {
    return Object.assign(new Error('Native session refresh failed'), {response: {status, data: {error: code}}});
}
function requestId(): string {
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
}
export function getNativeAccessToken(): string {
    if (!usesNativeRefreshBroker()) return '';
    try { return window.sessionStorage.getItem('access_token') || ''; } catch { return ''; }
}

export function resolveNativeRefreshMode(): Promise<boolean> {
    if (usesNativeRefreshBroker()) return Promise.resolve(true);
    if (legacy() || !bridge()) return Promise.resolve(false);
    if (probePromise) return probePromise;
    // Silence is not evidence of an old client. Do not issue a cookie refresh
    // while a current client is still injecting its capabilities.
    probePromise = new Promise<boolean>((resolve, reject) => {
        let timer: ReturnType<typeof setTimeout>;
        let settled = false;
        const finish = () => {
            if (settled) return;
            if (!usesNativeRefreshBroker() && !legacy()) return;
            settled = true; clearTimeout(timer); window.removeEventListener(BOOTSTRAP_EVENT, finish);
            resolve(usesNativeRefreshBroker());
        };
        const fail = () => {
            if (settled) return;
            settled = true; clearTimeout(timer); window.removeEventListener(BOOTSTRAP_EVENT, finish);
            reject(failure('native_refresh_unavailable'));
        };
        window.addEventListener(BOOTSTRAP_EVENT, finish);
        timer = setTimeout(fail, 1500);
        try { bridge().postMessage(JSON.stringify({v: 1, type: 'native_capability_probe', data: {requestId: requestId()}})); }
        catch { fail(); }
    }).finally(() => { probePromise = null; });
    return probePromise;
}

export function requestNativeRefresh(): Promise<{data: {access_token: string}}> {
    if (refreshPromise) return refreshPromise;
    if (!usesNativeRefreshBroker()) return Promise.reject(failure('native_refresh_unavailable'));
    const ownerEpoch = epoch();
    let id: string;
    try { id = requestId(); } catch { return Promise.reject(failure('native_refresh_unavailable')); }
    refreshPromise = new Promise<{data: {access_token: string}}>((resolve, reject) => {
        let settled = false;
        const finish = (error?: any, token?: string) => {
            if (settled) return;
            settled = true; clearTimeout(timer); window.removeEventListener(RESULT_EVENT, receive);
            if (error) reject(error);
            else {
                try { window.sessionStorage.removeItem('refresh_token'); window.sessionStorage.setItem('access_token', token!); } catch { /* memory token still usable */ }
                resolve({data: {access_token: token!}});
            }
        };
        const receive = (event: Event) => {
            const result = (event as CustomEvent).detail;
            if (!result || result.requestId !== id || result.epoch !== ownerEpoch) return;
            if (epoch() !== ownerEpoch) return finish(failure('native_session_changed'));
            if (result.error) return finish(failure(String(result.error), ['invalid_or_expired','token_reused','fingerprint_mismatch','session_unavailable'].includes(result.error) ? 401 : 503));
            const token = typeof result.accessToken === 'string' ? result.accessToken.trim() : '';
            if (!token || token.length > 16384) return finish(failure('native_refresh_failed'));
            finish(undefined, token);
        };
        const timer = setTimeout(() => finish(failure('native_refresh_timeout')), 30000);
        window.addEventListener(RESULT_EVENT, receive);
        try { bridge().postMessage(JSON.stringify({v: 1, type: 'native_refresh_request', data: {requestId: id, src: 'staff_refresh'}, epoch: ownerEpoch})); }
        catch { finish(failure('native_refresh_unavailable')); }
    }).finally(() => { refreshPromise = null; });
    return refreshPromise;
}
